import { useLocation } from 'tradera/hooks/use-location';
import { URL_LANGUAGES } from 'tradera/lang/constants.mjs';
import {
    selectEnvironment,
    selectFeatureSwitches
} from 'tradera/state/environment/selectors';
import { useAppSelector } from 'tradera/state/hooks';

export const NEXTWEB_PATHS = [
    '/causes',
    '/causes/items',
    '/brands',
    '/categories',
    '/how-to-buy',
    '/how-to-sell',
    '/my/overview-seller',
    '/my/wish-list',
    '/top-lists',
    '/my/picklist'
];

const PATH_PREFIX = `(/(${URL_LANGUAGES.join('|')}))?`;
const vipPathRegex = new RegExp(`${PATH_PREFIX}/item/[0-9]+/([0-9]+)/`);
const isVipLink = (pathname: string) => vipPathRegex.test(pathname);

const causePathRegex = new RegExp(`${PATH_PREFIX}/cause/`);
const isCauseLink = (pathname: string) => causePathRegex.test(pathname);

const charityPathRegex = new RegExp(`${PATH_PREFIX}/charity/`);
const isCharityLink = (pathname: string) => charityPathRegex.test(pathname);

const campaignPathRegex = new RegExp(`${PATH_PREFIX}/campaign/`);
const isCampaignLink = (pathname: string) => campaignPathRegex.test(pathname);

const shopPathRegex = new RegExp(`${PATH_PREFIX}/shop/`);
const isShopLink = (pathname: string) => shopPathRegex.test(pathname);

const topListsPathRegex = new RegExp(`${PATH_PREFIX}/top-lists/`);
const isTopListsLink = (pathname: string) => topListsPathRegex.test(pathname);

const brandPathRegex = new RegExp(`${PATH_PREFIX}/brand/`);
const isBrandLink = (pathname: string) => brandPathRegex.test(pathname);

const categoryPathRegex = new RegExp(`${PATH_PREFIX}/category/([0-9]+)`);
const isCategoryLink = (pathname: string) => categoryPathRegex.test(pathname);

const searchPathRegex = new RegExp(`${PATH_PREFIX}/search\\?q=.*`);
const isSearchLink = (pathname: string) => searchPathRegex.test(pathname);

const startPagePathRegex = new RegExp(`^${PATH_PREFIX}/?$`);
const isStartPageLink = (pathname: string) => startPagePathRegex.test(pathname);

const profilePageInformationPathRegex = new RegExp(
    `${PATH_PREFIX}/profile/information/`
);
const isProfilePageInformationLink = (pathname: string) =>
    profilePageInformationPathRegex.test(pathname);

const profilePageItemsPathRegex = new RegExp(`${PATH_PREFIX}/profile/items/`);
const isProfilePageItemsLink = (pathname: string) =>
    profilePageItemsPathRegex.test(pathname);

const profilePageFeedbackPathRegex = new RegExp(
    `${PATH_PREFIX}/profile/feedback/`
);

const detailedSearchPathRegex = new RegExp(`${PATH_PREFIX}/search/detailed$`);
const isDetailedSearchPageLink = (pathname: string) =>
    detailedSearchPathRegex.test(pathname);

const isProfilePageFeedbackLink = (pathname: string) =>
    profilePageFeedbackPathRegex.test(pathname);

const isLinkToNextWebPage = (url: URL) =>
    url.searchParams.get('next') === '1' ||
    NEXTWEB_PATHS.some((nextwebPath) => url.pathname.endsWith(nextwebPath));

export const isLastDigitEnabled = (
    digits: string,
    enabledDigits: Array<string>
) => enabledDigits.includes(digits.slice(-1));

export const useNextWebSpaLink = (absoluteUrl: URL) => {
    const location = useLocation();
    const toggles = useAppSelector(selectFeatureSwitches) || {};
    const pathname = absoluteUrl?.pathname;
    const { isNativeAppContext } = useAppSelector(selectEnvironment);

    if (isNativeAppContext) return false;

    if (isStartPageLink(pathname)) {
        return pathname !== location.pathname;
    }

    if (isVipLink(pathname)) return true;

    if (isCategoryLink(pathname)) return true;

    if (isCauseLink(pathname)) return true;

    if (isShopLink(pathname)) return true;

    if (isSearchLink(absoluteUrl.href)) return true;

    if (isCampaignLink(pathname)) return true;

    if (isCharityLink(pathname)) return true;

    if (isTopListsLink(pathname)) return true;

    if (isBrandLink(pathname)) return true;

    if (isProfilePageInformationLink(pathname)) return true;

    if (isProfilePageItemsLink(pathname)) return true;

    if (isProfilePageFeedbackLink(pathname)) return true;

    if (isDetailedSearchPageLink(pathname)) {
        return Boolean(toggles?.['next-web-detailed-search']);
    }

    return isLinkToNextWebPage(absoluteUrl);
};
